import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/api-client';
import {
  AWAITING_VALIDATION_LIMIT,
  awaitingValidation,
  awaitingValidationFilter,
  type MeetingLike,
} from '@/lib/portalMeetings';

/**
 * The client portal's « RDV à valider » (REV-W5-6): one query, one rule
 * (src/lib/portalMeetings.ts), shared by the home card, Mes rendez-vous ›
 * À valider and the menu badge, so the three always show the same count.
 * `scope` is who reads (the viewer's email); `customerId` narrows to one
 * customer (the agency's preview).
 */
export const PORTAL_AWAITING_QUERY_KEY = ['portal-awaiting-validation'] as const;

export interface AwaitingMeeting extends MeetingLike {
  id: string;
  prospect_name?: string | null;
  prospect_company?: string | null;
  agent_name?: string | null;
  meeting_date?: string | null;
  connected_at?: string | null;
  meeting_status?: string | null;
  calendar_event_url?: string | null;
  client_validation_status?: string | null;
  validation_history?: { action: string; note?: string; date: string; actor: string }[];
}

interface ParallelCallApi {
  filter(query: Record<string, unknown>, sort: string, limit: number): Promise<AwaitingMeeting[]>;
}

export function usePortalAwaitingMeetings({ scope, customerId = null, enabled = true }: {
  scope?: string | null;
  customerId?: string | null;
  enabled?: boolean;
}) {
  const query = useQuery<AwaitingMeeting[]>({
    queryKey: [...PORTAL_AWAITING_QUERY_KEY, scope ?? null, customerId ?? null],
    queryFn: () =>
      (api.entities as unknown as { ParallelCall: ParallelCallApi }).ParallelCall.filter(
        awaitingValidationFilter(customerId),
        '-started_at',
        AWAITING_VALIDATION_LIMIT,
      ),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const rows = useMemo(() => awaitingValidation(query.data ?? []), [query.data]);
  return { query, rows };
}
